import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "api";

const normalizeMunicipality = (municipality) => {
  if (!municipality) return null;

  return {
    ...municipality,
    id: municipality.id,
    nameEn: municipality.nameEn,
    nameGr: municipality.nameGr,
    regionId: municipality.regionId,
    prefectureId: municipality.prefectureId,
    regionNameEn: municipality.regionNameEn,
    regionNameGr: municipality.regionNameGr,
    prefectureNameEn: municipality.prefectureNameEn,
    prefectureNameGr: municipality.prefectureNameGr,
  };
};

const normalizeVarietyRead = (varietyRead) => {
  if (!varietyRead) return null;

  return {
    ...varietyRead,
    id: varietyRead.id,
    cropId: varietyRead.cropId,
    varietyId: varietyRead.varietyId,
    taxonomyId: varietyRead.taxonomyId,
    cropNameEn: varietyRead.cropNameEn,
    cropNameGr: varietyRead.cropNameGr,
    varietyNameEn: varietyRead.varietyNameEn,
    varietyNameGr: varietyRead.varietyNameGr,
    taxonomyNameEn: varietyRead.taxonomyNameEn,
    taxonomyNameGr: varietyRead.taxonomyNameGr,
  };
};

const normalizeFarmerDetails = (farmerDetails) => {
  const detailsList = Array.isArray(farmerDetails)
    ? farmerDetails
    : farmerDetails
      ? [farmerDetails]
      : [];

  return detailsList.map((farmer) => ({
    ...farmer,
    email: farmer.email,
    phone: farmer.phone,
    userId: farmer.userId,
    farmerId: farmer.farmerId,
    lastName: farmer.lastName,
    firstName: farmer.firstName,
    taxNumber: farmer.taxNumber,
  }));
};

const normalizeWateringMechanismRead = (wateringMechanismRead) => {
  if (!wateringMechanismRead) return null;

  return {
    ...wateringMechanismRead,
    id: wateringMechanismRead.id,
    coefficient: wateringMechanismRead.coefficient,
    wateringCategoryEn: wateringMechanismRead.wateringCategoryEn,
    wateringCategoryGr: wateringMechanismRead.wateringCategoryGr,
    wateringDescriptionEn: wateringMechanismRead.wateringDescriptionEn,
    wateringDescriptionGr: wateringMechanismRead.wateringDescriptionGr,
  };
};

const normalizeField = (field) => {
  const municipality = normalizeMunicipality(field.municipality);
  const varietyRead = normalizeVarietyRead(field.varietyRead);
  const farmerDetails = normalizeFarmerDetails(field.farmerDetails || []);
  const wateringMechanismRead = normalizeWateringMechanismRead(
    field.wateringMechanismRead,
  );

  return {
    ...field,
    id: field.id,
    name: field.name,
    cropId: field.cropId || varietyRead?.cropId,
    dateAdded: field.dateAdded,
    ecoschemes: field.ecoschemes || [],
    varietyId: field.varietyId,
    coordinates: field.coordinates || null,
    isBioCrop: field.isBioCrop ?? false,
    centerPoint: field.centerPoint || null,
    inputSource: field.inputSource,
    municipality,
    varietyRead,
    areaOfField: field.areaOfField,
    farmerDetails,
    numberOfRoots: field.numberOfRoots ?? 0,
    plantationDate: field.plantationDate,
    externalServices: field.externalServices || null,
    extraInformation: field.extraInformation || null,
    inputSourceName: field.inputSourceName,
    agronomistDetails: field.agronomistDetails || [],
    wateringMechanismId: field.wateringMechanismId,
    wateringMechanismRead,
  };
};

export const normalizeFieldsPayload = (payload) => {
  if (Array.isArray(payload)) return payload.map(normalizeField);

  if (Array.isArray(payload?.fields)) return payload.fields.map(normalizeField);

  if (Array.isArray(payload?.results))
    return payload.results.map(normalizeField);

  return [];
};

const normalizeWaterAction = (waterAction) => {
  if (!waterAction) return null;
  return {
    ...waterAction,
    quantityUnits: waterAction.quantityUnits,
  };
};

const normalizeDrugAction = (drugAction) => {
  if (!drugAction) return null;
  return {
    ...drugAction,
    brandName: drugAction.brandName,
  };
};

const normalizeFertilizeAction = (fertilizeAction) => {
  if (!fertilizeAction) return null;
  return {
    ...fertilizeAction,
    brandName: fertilizeAction.brandName,
  };
};

const normalizeObservationAction = (observationAction) => {
  if (!observationAction) return null;
  return observationAction;
};

const normalizeProposedAction = (proposedAction) => {
  if (!proposedAction) return null;
  return proposedAction;
};

const normalizeAction = (action) => {
  const waterAction = normalizeWaterAction(action.waterAction);
  const drugAction = normalizeDrugAction(action.drugAction);
  const fertilizeAction = normalizeFertilizeAction(action.fertilizeAction);
  const observationAction = normalizeObservationAction(
    action.observationAction,
  );
  const proposedAction = normalizeProposedAction(action.proposedAction);

  return {
    ...action,
    id: action.id,
    dateRange: action.dateRange,
    fieldId: action.fieldId,
    taskId: action.taskId,
    taskName: action.taskName,
    description: action.description,
    waterAction,
    drugAction,
    fertilizeAction,
    diagnoseAction: action.diagnoseAction,
    observationAction,
    proposedAction,
    datetimePerformed: action.datetimePerformed,
    datetimePerformedEnd: action.datetimePerformedEnd,
    priority: action.priority ?? 0,
    actionTypeName: action.actionTypeName,
    status: action.status,
    mild: action.mild,
    intensive: action.intensive,
    bio: action.bio,
  };
};

const normalizeActionsPayload = (payload) => {
  const collection = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];

  const hasNestedActions = collection.some(
    (item) =>
      Array.isArray(item?.actions?.results) || Array.isArray(item?.actions),
  );

  if (!hasNestedActions) {
    return collection.map(normalizeAction);
  }

  return collection.flatMap((item) => {
    if (Array.isArray(item?.actions?.results)) {
      return item.actions.results.map(normalizeAction);
    }

    if (Array.isArray(item?.actions)) {
      return item.actions.map(normalizeAction);
    }

    return [];
  });
};

export const useAgronomistDashboardFieldsQuery = () => {
  const query = useQuery({
    queryKey: ["agronomist-dashboard", "fields"],
    queryFn: async () => {
      const response = await api.fields.fetchMy();
      return response?.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const fields = useMemo(
    () => normalizeFieldsPayload(query.data),
    [query.data],
  );

  return {
    ...query,
    fields,
  };
};

export const useAgronomistDashboardActionsQuery = ({
  fieldIds = [],
  startDate,
  endDate,
} = {}) => {
  const normalizedFieldIds = Array.isArray(fieldIds)
    ? fieldIds.filter((fieldId) => fieldId !== null && fieldId !== undefined)
    : [];

  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "actions",
      normalizedFieldIds,
      startDate || null,
      endDate || null,
    ],
    queryFn: async () => {
      const response = await api.actions.fetch({
        fieldIds: normalizedFieldIds,
        startDate,
        endDate,
      });
      return response?.data;
    },
    enabled: normalizedFieldIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const actions = useMemo(
    () => normalizeActionsPayload(query.data),
    [query.data],
  );

  return {
    ...query,
    actions,
  };
};

export const useAgronomistDashboardWeatherQuery = ({
  fieldId,
  startDate,
  endDate,
} = {}) => {
  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "weather",
      fieldId || null,
      startDate || null,
      endDate || null,
    ],
    queryFn: async () => {
      const weatherPerDay = await api.weather.forecast.external(
        fieldId,
        startDate,
        endDate,
      );
      return weatherPerDay;
    },
    enabled: Boolean(fieldId && startDate && endDate),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const weatherByDay = useMemo(() => query.data || {}, [query.data]);

  return {
    ...query,
    weatherByDay,
  };
};

export const useAgronomistDashboardBiologicalStagesQuery = ({
  cropId,
  startDate,
  endDate,
  cutoff = false,
} = {}) => {
  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "biological-stages",
      cropId || null,
      startDate || null,
      endDate || null,
      cutoff,
    ],
    queryFn: async () => {
      const data = await api.biocycle.bioPhases({
        cropId,
        startDate,
        endDate,
        cutoff,
      });

      return data;
    },
    enabled: Boolean(cropId && startDate && endDate),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    biologicalStages: query.data || [],
  };
};

export const useAgronomistDashboardPlansQuery = ({
  cropIds = [],
  cropId,
  date,
} = {}) => {
  const normalizedCropIds = Array.isArray(cropIds)
    ? cropIds.filter((value) => value !== null && value !== undefined)
    : cropId
      ? [cropId]
      : [];

  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "plans",
      normalizedCropIds,
      date || null,
    ],
    queryFn: async () => {
      const response = await api.biocycle.dashboardActionPlan.fetch({
        cropIds: normalizedCropIds,
        date,
      });
      return response?.data;
    },
    enabled: normalizedCropIds.length > 0 && Boolean(date),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    plans: query.data,
  };
};

export const useAgronomistDashboardInsectAlertsMutation = () => {
  const mutation = useMutation({
    mutationKey: ["agronomist-dashboard", "insect-alerts"],
    mutationFn: async ({ cropId, fieldId, date } = {}) => {
      const response = await api.alerts.insectAlerts.predict({
        cropId,
        fieldId,
        date,
      });

      return response?.data;
    },
  });

  return {
    ...mutation,
    insectAlerts: mutation.data,
    fetchInsectAlerts: mutation.mutate,
    fetchInsectAlertsAsync: mutation.mutateAsync,
  };
};

export const useAgronomistDashboardStressAlertsMutation = () => {
  const mutation = useMutation({
    mutationKey: ["agronomist-dashboard", "stress-alerts"],
    mutationFn: async ({ cropId, fieldId, date } = {}) => {
      const response = await api.alerts.stressAlerts.predict({
        cropId,
        fieldId,
        date,
      });

      return response?.data;
    },
  });

  return {
    ...mutation,
    stressAlerts: mutation.data,
    fetchStressAlerts: mutation.mutate,
    fetchStressAlertsAsync: mutation.mutateAsync,
  };
};

export const useAgronomistDashboardActionAlertsMutation = () => {
  const mutation = useMutation({
    mutationKey: ["agronomist-dashboard", "action-alerts"],
    mutationFn: async ({ cropId, fieldId, date } = {}) => {
      const response = await api.alerts.actionAlerts.predict({
        cropId,
        fieldId,
        date,
      });

      return response?.data;
    },
  });

  return {
    ...mutation,
    actionAlerts: mutation.data,
    fetchActionAlerts: mutation.mutate,
    fetchActionAlertsAsync: mutation.mutateAsync,
  };
};

export const useAgronomistDashboardWeatherAlertsQuery = (
  fieldId,
  startDate,
) => {
  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "weather-alerts",
      fieldId,
      startDate || null,
    ],
    queryFn: async () => {
      const response = await api.alerts.weatherAlerts.predict({
        fieldId,
        startDate,
      });
      return response?.data;
    },
    enabled: Boolean(fieldId && startDate),
  });

  return {
    ...query,
    weatherAlerts: query.data,
  };
};

export const useAgronomistDashboardIrrigationNeedAlertsQuery = ({
  fieldIds = [],
  days,
} = {}) => {
  const normalizedFieldIds = Array.isArray(fieldIds)
    ? fieldIds.filter((fieldId) => fieldId !== null && fieldId !== undefined)
    : [];

  const query = useQuery({
    queryKey: [
      "agronomist-dashboard",
      "irrigation-need-alerts",
      normalizedFieldIds,
      days,
    ],
    queryFn: async () => {
      const response = await api.alerts.irrigationNeedAlerts.predict({
        fieldIds: normalizedFieldIds,
        days,
      });
      return response?.data;
    },
    enabled: normalizedFieldIds.length > 0,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    irrigationNeedAlerts: query.data,
  };
};
